<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import store from '@/store';
import Modal from '@/components/shared/Dialog.vue';
import PaymentAddedModal from '@/pages/payments/components/PaymentAddedModal.vue';
import iconGlassGraphUp from '@/assets/icons/icon-glass-graph-up.png';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import { RouteNames } from '@/data/routes';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

const router = useRouter();
const visible = ref(false);
const closedViaPrimaryButton = ref(false);

const commonAnalyticsProperties = computed(() => ({
  businessId: store.getters.business?.id,
  businessName: store.getters.business?.name,
  modalContext: 'page_load',
}));

const modalWasDismissed = LocalStorage.getItem(
  LocalStorageKeys.DISMISS_REVENUE_ACCOUNT_MODAL_THIS_SESSION,
);

onMounted((): void => {
  if (store.getters.isMissingRevenueAccountConnection && !modalWasDismissed) {
    visible.value = true;
  }
});

function onPrimaryClick(): void {
  closedViaPrimaryButton.value = true;
  visible.value = false;
  LocalStorage.setItem(
    LocalStorageKeys.DISMISS_REVENUE_ACCOUNT_MODAL_THIS_SESSION,
    true,
  );
  router.push({ name: RouteNames.CONNECTED_ACCOUNTS.INDEX });
  analytics.track(
    SEGMENT_EVENTS.if_connect_revenue_account_clicked,
    commonAnalyticsProperties.value,
  );
}

function onHide(): void {
  if (!closedViaPrimaryButton.value) {
    LocalStorage.setItem(
      LocalStorageKeys.DISMISS_REVENUE_ACCOUNT_MODAL_THIS_SESSION,
      true,
    );
    analytics.track(
      SEGMENT_EVENTS.if_connect_revenue_account_dismissed,
      commonAnalyticsProperties.value,
    );
  }
  closedViaPrimaryButton.value = false;
}
</script>

<template>
  <Modal
    v-model:visible="visible"
    :closable="true"
    :modal="true"
    append-to="self"
    data-payment-added-modal
    @hide="onHide"
  >
    <PaymentAddedModal
      title="Connect a Revenue Account to Unlock Funding"
      :image="iconGlassGraphUp"
      image-alt="Icon showing a glass with an upward graph"
      :button-primary="{
        label: 'Connect a revenue account',
      }"
      @primary-click="onPrimaryClick"
    >
      <template #body>
        <p>
          Clearco uses a data-driven approach to generate your funding capacity.
          You must connect at least one e-commerce sales platform to unlock
          funding and avoid delays.
        </p>
      </template>
    </PaymentAddedModal>
  </Modal>
</template>
